import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sessionPolicyApi } from '../services/sessionPolicyApi';
import type { SessionPolicySettings, SessionPolicyUpdatePayload } from '../types/sessionPolicy';

export const sessionPolicyKeys = {
  all: ['session-policy'] as const,
  detail: (projectId: string) => [...sessionPolicyKeys.all, projectId] as const,
};

export function useSessionPolicy(projectId: string | undefined) {
  return useQuery({
    queryKey: sessionPolicyKeys.detail(projectId!),
    queryFn: async (): Promise<SessionPolicySettings> => {
      const response = await sessionPolicyApi.get(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load session policy');
      }
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useUpdateSessionPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: SessionPolicyUpdatePayload;
    }) => {
      const response = await sessionPolicyApi.update(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update session policy');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: sessionPolicyKeys.detail(variables.projectId),
      });
    },
  });
}
